import { Mesh } from 'three';
import { difference, intersection, fromRing } from '../../geo/clip';
import type { PolygonWithHoles } from '../../geo/types';
import { ringBounds } from '../../geo/polygon';
import type { GroundPiece, SiteModel, LandHeight } from '../../site/types';
import type { PaletteModel } from '../../data/types';
import type { MaterialFactory } from '../../render/types';
import { createMeshBuilder } from '../mesh/meshBuilder';
import { WORLD } from '../../core/config';
import type { DensifyConfig } from './densify';
import { groundStyle, type FieldRegionClasses } from './groundStyle';
import { withDitchContrast } from './ditchContrast';
import { withHeightRefinement } from './heightRefinement';

const FULL_DITCH_CONTRAST = 1;

/** Split only the changing terrain area out of large countryside polygons. Refining a whole
 * polder because one corner touches the city can add millions of unnecessary triangles. */
export function groundDrawParts(
  piece: GroundPiece,
  heightAt: LandHeight
): readonly { polygon: PolygonWithHoles; raised: boolean }[] {
  if (piece.kind === 'street' || piece.kind === 'square' || !heightAt.elevationBounds)
    return [{ polygon: piece.polygon, raised: true }];
  const b = heightAt.elevationBounds,
    box = ringBounds(piece.polygon.outer);
  if (box.minX > b.maxX || box.maxX < b.minX || box.minY > b.maxY || box.maxY < b.minY)
    return [{ polygon: piece.polygon, raised: false }];
  if (box.minX >= b.minX && box.maxX <= b.maxX && box.minY >= b.minY && box.maxY <= b.maxY)
    return [{ polygon: piece.polygon, raised: true }];
  const local = fromRing([
    [b.minX, b.minY],
    [b.maxX, b.minY],
    [b.maxX, b.maxY],
    [b.minX, b.maxY],
  ]);
  return [
    ...intersection([piece.polygon], [local]).map((polygon) => ({ polygon, raised: true })),
    ...difference([piece.polygon], [local]).map((polygon) => ({ polygon, raised: false })),
  ];
}

export function baseGroundRefinement(config: DensifyConfig | undefined, heightAt: LandHeight) {
  const relief = withHeightRefinement(config, heightAt);
  return {
    ...relief,
    config: { ...relief.config, surface: { ...relief.config.surface!, tileSize: 64 } },
  };
}

/** Append bounded build jobs to the same tile mesh, keeping the final draw and geometry order. */
export function createGroundMeshAccumulator(
  palette: PaletteModel,
  materials: MaterialFactory,
  fields: FieldRegionClasses,
  densifyConfig?: DensifyConfig,
  heightAt?: LandHeight,
  baseHeightAt: LandHeight | undefined = heightAt
) {
  const builder = createMeshBuilder();
  const relief = heightAt ? withHeightRefinement(densifyConfig, heightAt) : undefined;
  const base = baseHeightAt ? baseGroundRefinement(densifyConfig, baseHeightAt) : undefined;
  return {
    append(pieces: readonly GroundPiece[]): void {
      for (const piece of pieces) {
        const street = piece.kind === 'street' || piece.kind === 'square';
        const profile = street ? relief : base;
        const parts = heightAt
          ? groundDrawParts(piece, heightAt)
          : [{ polygon: piece.polygon, raised: false }];
        for (const part of parts)
          builder.groundPolygon(
            groundStyle(piece, palette, fields),
            part.polygon,
            part.raised && profile ? profile.heightAt : WORLD.groundY,
            part.raised && profile ? profile.config : densifyConfig
          );
      }
    },
    finish(): Mesh {
      const contrasted = withDitchContrast(builder.finish(), () => FULL_DITCH_CONTRAST);
      const mesh = new Mesh(contrasted, materials.painted());
      mesh.matrixAutoUpdate = false;
      mesh.frustumCulled = true;
      return mesh;
    },
  };
}

function groundMeshOf(
  pieces: readonly GroundPiece[],
  palette: PaletteModel,
  materials: MaterialFactory,
  fields: FieldRegionClasses,
  densifyConfig?: DensifyConfig,
  heightAt?: LandHeight,
  baseHeightAt: LandHeight | undefined = heightAt
): Mesh {
  const tile = createGroundMeshAccumulator(
    palette,
    materials,
    fields,
    densifyConfig,
    heightAt,
    baseHeightAt
  );
  tile.append(pieces);
  return tile.finish();
}

export function buildGroundMesh(
  site: SiteModel,
  palette: PaletteModel,
  materials: MaterialFactory,
  densifyConfig?: DensifyConfig,
  fields: FieldRegionClasses = {}
): Mesh {
  return groundMeshOf(
    site.ground,
    palette,
    materials,
    fields,
    densifyConfig,
    site.queries.landHeightAt ?? ((p) => Math.max(0, site.queries.groundHeightAt(p))),
    site.queries.baseHeightAt
  );
}

export function buildGroundMeshFromPieces(
  pieces: readonly GroundPiece[],
  palette: PaletteModel,
  materials: MaterialFactory,
  densifyConfig?: DensifyConfig,
  fields: FieldRegionClasses = {},
  heightAt?: LandHeight,
  baseHeightAt: LandHeight | undefined = heightAt
): Mesh {
  return groundMeshOf(pieces, palette, materials, fields, densifyConfig, heightAt, baseHeightAt);
}
